import { FormattedMessage } from '@99designs/common/i18n';
import { SquarespaceNeeds } from '__generated__/types';
import { Currency } from 'common/utils/currency';
import { Price } from 'projects/components/BudgetSlider/PriceGuide';
import { ObservableForm } from 'projects/components/ObservableForm';
import React from 'react';
import BoxSelectField from './BoxSelectField';

// Recommended minimum USD price for a given project type.
export function minPriceForProjectType(projectType: SquarespaceNeeds): number {
    switch (projectType) {
        case SquarespaceNeeds.SEO:
            return 30000;
        case SquarespaceNeeds.NEW_SITE:
            return 120000;
        case SquarespaceNeeds.DESIGNER_REVIEW:
            return 30000;
        case SquarespaceNeeds.SCHEDULING:
            return 10000;
        case SquarespaceNeeds.TRAINING_AND_SUPPORT:
            return 10000;
        case SquarespaceNeeds.SITE_FUNCTIONALITY:
            return 40000;
        default:
            throw `missing minimum price for ${projectType}`;
    }
}

interface Fields {
    needs: SquarespaceNeeds;
}

interface Props<T> {
    title: string;
    form: ObservableForm<T>;
}

const needs = {
    [SquarespaceNeeds.MINOR_CHANGES]: __(
        'Tweaks like content spacing, connect a domain, add a form, add a custom font, etc.'
    ),
    [SquarespaceNeeds.SEO]: __(
        'An audit on an existing site or a consultation on your long-term SEO strategy'
    ),
    [SquarespaceNeeds.NEW_SITE]: __(
        'New website or online store, redesign an existing site, or migrate a site to Squarespace'
    ),
    [SquarespaceNeeds.DESIGNER_REVIEW]: __(
        'Professional audit, site refresh, custom design work, finishing touches, etc.'
    ),
    [SquarespaceNeeds.ONLINE_STORE_SETUP]: __(
        'Add ecommerce features to an existing site-add products, set up payments, customize categories, etc.'
    ),
    [SquarespaceNeeds.SCHEDULING]: __(
        'Set up an online calendar to book appointments and accept payments on a Squarespace site'
    ),
    [SquarespaceNeeds.SITE_FUNCTIONALITY]: __(
        'Updates to your existing site layout, third-party integrations, extensions, custom code, UX/UI, etc.'
    ),
    [SquarespaceNeeds.TRAINING_AND_SUPPORT]: __(
        'Personalized web training and general help with your Squarespace products'
    ),

    // Deprecated
    [SquarespaceNeeds.REDESIGN_SITE]: __(
        'Completely redesign an existing Squarespace site. (New fonts, colors or styles.)'
    ),
    // Deprecated
    [SquarespaceNeeds.RECREATE_SITE]: __(
        'Recreate an existing site from another service on Squarespace.'
    ),
    // Deprecated
    [SquarespaceNeeds.FINISH_TRIAL_SITE]: __('Finish creating an existing Squarespace trial site.'),
    // Deprecated
    [SquarespaceNeeds.MAJOR_CHANGES]: __(
        'Add multiple pages or connect a complex third-party integration'
    ),
};

SquarespaceNeedsField.labelFor = (value: SquarespaceNeeds) => needs[value];

export default function SquarespaceNeedsField<T extends Fields>({ form, title }: Props<T>) {
    return (
        <BoxSelectField
            form={form}
            name="needs"
            title={title}
            required
            options={[
                {
                    value: SquarespaceNeeds.MINOR_CHANGES,
                    label: needs[SquarespaceNeeds.MINOR_CHANGES],
                    title: __('Small Site Tasks – Prices vary'),
                },
                {
                    value: SquarespaceNeeds.SEO,
                    label: needs[SquarespaceNeeds.SEO],
                    title: (
                        <FormattedMessage
                            message={__('Search Engine Optimization (SEO) – From {price}')}
                            values={{
                                price: (
                                    <Price
                                        amount={minPriceForProjectType(SquarespaceNeeds.SEO)}
                                        currency={Currency.USD}
                                    />
                                ),
                            }}
                        />
                    ),
                },
                {
                    value: SquarespaceNeeds.NEW_SITE,
                    label: needs[SquarespaceNeeds.NEW_SITE],
                    title: (
                        <FormattedMessage
                            message={__('Full Site Builds – From {price}')}
                            values={{
                                price: (
                                    <Price
                                        amount={minPriceForProjectType(SquarespaceNeeds.NEW_SITE)}
                                        currency={Currency.USD}
                                    />
                                ),
                            }}
                        />
                    ),
                },
                {
                    value: SquarespaceNeeds.DESIGNER_REVIEW,
                    label: needs[SquarespaceNeeds.DESIGNER_REVIEW],
                    title: (
                        <FormattedMessage
                            message={__('Design Review – From {price}')}
                            values={{
                                price: (
                                    <Price
                                        amount={minPriceForProjectType(
                                            SquarespaceNeeds.DESIGNER_REVIEW
                                        )}
                                        currency={Currency.USD}
                                    />
                                ),
                            }}
                        />
                    ),
                },
                {
                    value: SquarespaceNeeds.ONLINE_STORE_SETUP,
                    label: needs[SquarespaceNeeds.ONLINE_STORE_SETUP],
                    title: __('Online Store Setup – Prices vary'),
                },
                {
                    value: SquarespaceNeeds.SCHEDULING,
                    label: needs[SquarespaceNeeds.SCHEDULING],
                    title: (
                        <FormattedMessage
                            message={__('Scheduling – From {price}')}
                            values={{
                                price: (
                                    <Price
                                        amount={minPriceForProjectType(SquarespaceNeeds.SCHEDULING)}
                                        currency={Currency.USD}
                                    />
                                ),
                            }}
                        />
                    ),
                },
                {
                    value: SquarespaceNeeds.SITE_FUNCTIONALITY,
                    label: needs[SquarespaceNeeds.SITE_FUNCTIONALITY],
                    title: (
                        <FormattedMessage
                            message={__('Site Functionality – From {price}')}
                            values={{
                                price: (
                                    <Price
                                        amount={minPriceForProjectType(
                                            SquarespaceNeeds.SITE_FUNCTIONALITY
                                        )}
                                        currency={Currency.USD}
                                    />
                                ),
                            }}
                        />
                    ),
                },
                {
                    value: SquarespaceNeeds.TRAINING_AND_SUPPORT,
                    label: needs[SquarespaceNeeds.TRAINING_AND_SUPPORT],
                    title: (
                        <FormattedMessage
                            message={__('Training and Support – From {price}')}
                            values={{
                                price: (
                                    <Price
                                        amount={minPriceForProjectType(
                                            SquarespaceNeeds.TRAINING_AND_SUPPORT
                                        )}
                                        currency={Currency.USD}
                                    />
                                ),
                            }}
                        />
                    ),
                },
            ]}
        />
    );
}
